// Imports ------------------------------------------------------------------->
import { Service, service, inject } from './di'
import { fetchJSON } from '../utils/fetch'
import { ConfigService } from './ConfigService'

// Private Definitions ------------------------------------------------------->

// Public Definitions -------------------------------------------------------->
export interface CityJSON {
    country: string
    state: string
    city: string
    fallback_postal_code: string
    geo_codes: string[]
}

@service('SeoService')
export class SeoService extends Service {
    @inject configService: ConfigService
    private _cityCache: { [key: string]: CityJSON } = {}
    private _seoContentCache: { [key: string]: string } = {}
    private locales = ['en-ca', 'fr-ca', 'en-us']

    private siteLocale: string = null

    setDescription(textContent: string) {
        const metaDescription = document.querySelector('meta[name=description]')
        const metaOgDescription = document.querySelector(
            'meta[name=og\\:description]'
        )

        if (metaDescription)
            metaDescription.setAttribute('content', textContent)

        if (metaOgDescription)
            metaOgDescription.setAttribute('content', textContent)
    }

    // Some google links appear to badly treat our URLs and append a \
    // This appears usually at the end of a link and seems to infect our
    // postal_code and locale values occasionally.
    // This helper function slices this off if we find it.
    fixTrailingSlash(value: string): string {
        if (value && value.substr(-1) === '\\') {
            return value.substr(0, value.length - 1)
        }

        return value
    }

    hasLocale() {
        return this.siteLocale !== null
    }

    setLocale(value: string) {
        this.siteLocale = this.isLocale(value) ? value : 'en-ca'
        document.documentElement.setAttribute('lang', this.siteLocale)
    }

    isLocale(value: string) {
        return this.locales.includes(value)
    }

    get locale() {
        return this.siteLocale
    }

    get inCity() {
        return document.documentElement.getAttribute('in-city')
    }

    set inCity(value: string) {
        document.documentElement.setAttribute('in-city', value)
    }

    getCacheKey = (state: string, city: string) => `${city}_${state}`

    async getCityInfo(state: string, city: string): Promise<CityJSON> {
        const cacheKey = this.getCacheKey(state, city)
        const cached: CityJSON = this._cityCache[cacheKey]
        if (cached) {
            return Promise.resolve(cached)
        }

        const cityParsed = city
            .split('_')
            .map((c) => c.toLowerCase())
            .join('_')
        const domain =
            this.configService.HOSTNAME === 'localhost'
                ? 'https://staging.flipp.com'
                : ''
        return fetchJSON(
            `${domain}/data/cities/${state.toLowerCase()}_${cityParsed}.json`
        )
            .then((city: CityJSON) => {
                this._cityCache[cacheKey] = city

                return Promise.resolve(city)
            })
            .catch((e) => {
                console.log(e)
                return Promise.resolve(null)
            })
    }

    async getSeoContent(
        store: string,
        locale: string,
        location: string
    ): Promise<string> {
        if (locale === 'en-us') {
            return Promise.resolve('')
        }
        const cacheKey = `${locale}_${location}_${store}`
        const cached = this._seoContentCache[cacheKey]
        if (cached) {
            return Promise.resolve(cached)
        }
        let url = `${this.configService.NEXTJS_API}/seo-content/${locale}/${store}`
        if (location) {
            url += `/${location}`
        }

        return fetchJSON(url)
            .then((content: { text: string }) => {
                this._seoContentCache[cacheKey] = content.text

                return Promise.resolve(content.text)
            })
            .catch(() => {
                return Promise.resolve('')
            })
    }
}
